import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { isSupabaseConfigured } from 'shared/supabase/client';
import type { User } from 'types';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const TOKEN_KEY = 'platform_auth_token';
const USER_KEY = 'platform_auth_user';

function loadStoredAuth(): Pick<AuthState, 'user' | 'token' | 'isAuthenticated'> {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    const userJson = localStorage.getItem(USER_KEY);
    if (token && userJson) {
      return { token, user: JSON.parse(userJson) as User, isAuthenticated: true };
    }
  } catch {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }
  return { token: null, user: null, isAuthenticated: false };
}

const stored = loadStoredAuth();

const initialState: AuthState = {
  ...stored,
  // Wait for Supabase session restore after OAuth redirect before AuthGuard redirects
  isLoading: isSupabaseConfigured && !stored.isAuthenticated,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (state, action: PayloadAction<{ user: User; token: string }>) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.isAuthenticated = true;
      state.isLoading = false;
      localStorage.setItem(TOKEN_KEY, action.payload.token);
      localStorage.setItem(USER_KEY, JSON.stringify(action.payload.user));
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    switchRole: (state, action: PayloadAction<string>) => {
      if (state.user) {
        state.user = { ...state.user, roles: [action.payload] };
        localStorage.setItem(USER_KEY, JSON.stringify(state.user));
      }
    },
    setUserTimezone: (state, action: PayloadAction<string>) => {
      if (state.user) {
        state.user = {
          ...state.user,
          timezone: action.payload,
          timezoneJustDetected: false,
        };
        localStorage.setItem(USER_KEY, JSON.stringify(state.user));
      }
    },
    acknowledgeTimezoneDetection: (state) => {
      if (state.user) {
        state.user = { ...state.user, timezoneJustDetected: false };
        localStorage.setItem(USER_KEY, JSON.stringify(state.user));
      }
    },
  },
});

export const {
  setCredentials,
  logout,
  setLoading,
  switchRole,
  setUserTimezone,
  acknowledgeTimezoneDetection,
} = authSlice.actions;
export default authSlice.reducer;
